/**
 * ChakraEventSequenceManager - Manages event sequences and dependencies
 *
 * Purpose:
 * - Register event sequences (A → B → C)
 * - Handle cascade dependencies (Nakshatra → Pada)
 * - Prevent duplicate events
 * - No setTimeout - pure event-driven
 *
 * Example:
 * When Nakshatra is enabled and Pada was ON:
 *   nakshatra.chakra.toggle(true) → pada.toggle(true)
 *
 * When Nakshatra is disabled:
 *   nakshatra.chakra.toggle(false) → pada.toggle(false) (cascade)
 */

import { EventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import type { EventSubscription } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import { devLog, devWarn } from '../utils/devLog';

interface EventSequenceRule {
    id: string;
    triggerEvent: string;                    // Event type that triggers this sequence
    condition?: (event: any) => boolean;     // Optional condition to check
    actions: EventAction[];                   // Actions to perform
    description?: string;                     // For debugging
}

interface EventAction {
    type: 'emit' | 'delay' | 'conditional';
    eventType?: string;                       // For 'emit' type
    getData?: (triggerEvent: any) => any;    // Generate event data
    condition?: (triggerEvent: any) => boolean; // For 'conditional' type
    delayMs?: number;                         // For 'delay' type (should avoid!)
}

export class ChakraEventSequenceManager {
    private eventStream: EventStream;
    private rules: Map<string, EventSequenceRule[]> = new Map();
    private subscriptions: EventSubscription[] = [];
    private processingEvents: Set<string> = new Set(); // Prevent infinite loops

    constructor(eventStream: EventStream) {
        this.eventStream = eventStream;
        this.setupDefaultRules();
    }

    /**
     * Setup default cascade rules for chakra dependencies
     */
    private setupDefaultRules(): void {
        // RULE 1: When Nakshatra is disabled → cascade disable Pada
        this.registerRule({
            id: 'nakshatra-disable-cascade-pada',
            triggerEvent: 'nakshatra.chakra.toggle',
            condition: (event) => {
                const showChakra = event.data?.showNAKSHATRAChakra ?? false;
                return !showChakra; // Only trigger when disabling
            },
            actions: [
                {
                    type: 'emit',
                    eventType: 'nakshatra.pada.toggle',
                    getData: (triggerEvent) => {
                        // CRITICAL: Also save to localStorage to prevent re-enable
                        const chartId = triggerEvent.chartId;
                        const multipleChartsContext = window.multipleChartsContext;

                        if (multipleChartsContext?.updateChart && multipleChartsContext.charts.has(chartId)) {
                            const chart = multipleChartsContext.charts.get(chartId);
                            if (chart) {
                                multipleChartsContext.updateChart(chartId, {
                                    settings: {
                                        ...chart.settings,
                                        showNakshatraPada: false
                                    }
                                });
                            }
                        }

                        return {
                            chartId: triggerEvent.chartId,
                            showPada: false
                        };
                    }
                }
            ],
            description: 'When Nakshatra is disabled, cascade disable Pada'
        });

        // RULE 2: DEPRECATED - replaced by Rule 3
        // Rule 2 was triggering pada toggle before renderer was created (async timing issue)
        // Now Rule 3 listens to chakra.universal.rendered which fires AFTER renderer is ready

        // RULE 3: When Nakshatra chakra is RENDERED AND Pada was ON → restore Pada
        // This fixes timing issue: renderer must be created and subscribed before pada event
        this.registerRule({
            id: 'nakshatra-rendered-restore-pada',
            triggerEvent: 'chakra.universal.rendered',
            condition: (event) => {
                // Only for nakshatra chakra
                if (event.chakraType !== 'nakshatra') {
                    return false;
                }

                const chartId = event.chartId;

                // Check if Pada was ON in chart settings (from localStorage)
                const multipleChartsContext = window.multipleChartsContext;
                if (!multipleChartsContext?.charts.has(chartId)) {
                    return false;
                }

                const chart = multipleChartsContext.charts.get(chartId);
                const padaSetting = chart?.settings?.showNakshatraPada ?? false;

                return padaSetting; // Only emit if Pada toggle is ON
            },
            actions: [
                {
                    type: 'emit',
                    eventType: 'nakshatra.pada.toggle',
                    getData: (triggerEvent) => ({
                        chartId: triggerEvent.chartId,
                        showPada: true
                    })
                }
            ],
            description: 'When Nakshatra chakra is fully rendered and Pada toggle is ON, restore Pada'
        });
    }

    /**
     * Register a new event sequence rule
     */
    public registerRule(rule: EventSequenceRule): void {
        const existing = this.rules.get(rule.triggerEvent) || [];
        existing.push(rule);
        this.rules.set(rule.triggerEvent, existing);

        // Subscribe to the trigger event and store for cleanup
        const subscription = this.eventStream.subscribe(rule.triggerEvent, (event: any) => {
            this.handleEvent(rule, event);
        });
        this.subscriptions.push(subscription);
    }

    /**
     * Handle event and execute sequence
     */
    private handleEvent(rule: EventSequenceRule, event: any): void {
        // Prevent infinite loops
        const eventKey = `${rule.id}_${event.id}`;
        if (this.processingEvents.has(eventKey)) {
            devLog(`⚠️ ChakraEventSequenceManager: Skipping duplicate event processing for ${eventKey}`);
            return;
        }

        // Check condition
        if (rule.condition && !rule.condition(event)) {
            return; // Condition not met, skip
        }

        this.processingEvents.add(eventKey);

        try {
            // Execute actions
            for (const action of rule.actions) {
                this.executeAction(action, event);
            }
        } finally {
            // Clean up immediately after execution
            this.processingEvents.delete(eventKey);
        }
    }

    /**
     * Execute a single action
     */
    private executeAction(action: EventAction, triggerEvent: any): void {
        switch (action.type) {
            case 'emit':
                if (action.eventType && action.getData) {
                    const eventData = {
                        id: `${action.eventType}-${triggerEvent.chartId}-${Date.now()}`,
                        type: action.eventType,
                        source: 'chakra-event-sequence-manager',
                        chartId: triggerEvent.chartId,
                        timestamp: Date.now(),
                        data: action.getData(triggerEvent)
                    };

                    this.eventStream.emit(eventData);
                }
                break;

            case 'conditional':
                if (action.condition && action.condition(triggerEvent)) {
                    // Execute nested actions or emit event
                    devLog(`✅ ChakraEventSequenceManager: Conditional action passed`);
                }
                break;

            case 'delay':
                // Avoid delays in event-driven system, but support if absolutely needed
                devWarn(`⚠️ ChakraEventSequenceManager: Delay actions should be avoided in event-driven architecture`);
                break;
        }
    }

    /**
     * Clear all rules and unsubscribe from events
     */
    public clearRules(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions = [];
        this.rules.clear();
        this.processingEvents.clear();
        devLog('ChakraEventSequenceManager: All rules cleared and subscriptions unsubscribed');
    }

    /**
     * Full cleanup - call on unmount/destruction
     */
    public destroy(): void {
        this.clearRules();
        devLog('ChakraEventSequenceManager: Destroyed');
    }

    /**
     * Get all registered rules (for debugging)
     */
    public getRules(): Map<string, EventSequenceRule[]> {
        return this.rules;
    }
}

// Global singleton instance
let sequenceManager: ChakraEventSequenceManager | null = null;

export const initChakraEventSequenceManager = (eventStream: EventStream): ChakraEventSequenceManager => {
    if (!sequenceManager) {
        sequenceManager = new ChakraEventSequenceManager(eventStream);
        window.chakraEventSequenceManager = sequenceManager;
    }
    return sequenceManager;
};

export const getChakraEventSequenceManager = (): ChakraEventSequenceManager | null => {
    return sequenceManager || window.chakraEventSequenceManager || null;
};
